// NIST Cybersecurity Framework 2.0 — 116 subcategory explanations
const NIST_CSF_EXPLANATIONS = {

  // ── GOVERN — Organizational Context ──────────────────────────────────────────
  'GV.OC-01': {
    plain_english: 'Document why your organization exists, what stakeholders expect, and what laws or contracts require you to do for cybersecurity.',
    example: 'A healthcare SaaS company documents its mission, lists HIPAA and SOC 2 obligations, and captures that customers expect 99.9% uptime and breach notification within 72 hours.',
    evidence_tips: [
      'Mission and vision statement referencing cybersecurity obligations',
      'Documented list of applicable regulations and contractual requirements',
      'Stakeholder expectations captured in risk management charter',
    ],
    common_mistakes: 'Organizations list regulations without explaining how those requirements actually shape day-to-day security decisions.',
    nist_reference: 'GV.OC-01',
  },
  'GV.OC-02': {
    plain_english: 'Identify everyone who cares about your security posture — customers, regulators, board members, partners — and document what each group expects.',
    example: 'A fintech maps stakeholders: customers expect data privacy, regulators expect SOX compliance, investors expect risk disclosures, and partners expect API security certifications.',
    evidence_tips: [
      'Stakeholder register identifying internal and external parties',
      'Documented security expectations per stakeholder group',
      'Board/executive briefing records on cybersecurity expectations',
    ],
    common_mistakes: 'Only listing internal stakeholders and ignoring regulators, customers, and supply chain partners who have material security expectations.',
    nist_reference: 'GV.OC-02',
  },
  'GV.OC-03': {
    plain_english: 'Compile all legal, regulatory, and contractual cybersecurity requirements in one place so nothing gets missed.',
    example: 'Legal and security jointly maintain a compliance matrix covering GDPR, PCI DSS, state privacy laws, and key customer contract clauses, reviewed quarterly.',
    evidence_tips: [
      'Compliance obligation register with requirement source, owner, and due date',
      'Legal review sign-off on cybersecurity requirements inventory',
      'Contract review process that flags new security obligations',
    ],
    common_mistakes: 'Maintaining separate lists per team so requirements get missed when contracts change or new regulations come into effect.',
    nist_reference: 'GV.OC-03',
  },
  'GV.OC-04': {
    plain_english: 'When identifying risks, consider your critical business objectives and what dependencies could disrupt them.',
    example: 'A logistics company identifies that a ransomware attack on its dispatch system would halt all deliveries, so route optimization and carrier integration are treated as critical assets.',
    evidence_tips: [
      'Business impact analysis linking IT systems to business objectives',
      'Critical dependency mapping showing upstream/downstream impacts',
      'Risk assessments that reference business context, not just technical metrics',
    ],
    common_mistakes: 'Risk assessments that focus on technical vulnerabilities without connecting them to business impact or mission criticality.',
    nist_reference: 'GV.OC-04',
  },
  'GV.OC-05': {
    plain_english: 'Your cybersecurity goals should directly support your business strategy and risk appetite, not exist in a separate silo.',
    example: 'A startup in rapid growth mode accepts higher risk on internal tools but invests heavily in protecting customer data because customer trust is the core business asset.',
    evidence_tips: [
      'Cybersecurity strategy document with explicit alignment to business goals',
      'Risk appetite statement approved by executive leadership',
      'Security budget allocation tied to business priorities',
    ],
    common_mistakes: 'Security strategy written in isolation by IT, then presented to leadership rather than co-developed with business owners.',
    nist_reference: 'GV.OC-05',
  },

  // ── GOVERN — Risk Management Strategy ─────────────────────────────────────────
  'GV.RM-01': {
    plain_english: 'Have a documented, leadership-approved strategy for how you identify, assess, respond to, and monitor cybersecurity risks.',
    example: 'The CISO publishes a risk management strategy that defines the risk framework (NIST RMF), escalation thresholds, and acceptable risk treatment options, approved by the CEO.',
    evidence_tips: [
      'Signed risk management strategy document with executive approval',
      'Risk framework selection rationale (NIST RMF, ISO 31000, etc.)',
      'Annual review records showing the strategy is kept current',
    ],
    common_mistakes: 'Writing a strategy document once and never reviewing it as the threat landscape or business context changes.',
    nist_reference: 'GV.RM-01',
  },
  'GV.RM-02': {
    plain_english: 'Define how much cybersecurity risk your organization is willing to accept, get leadership sign-off, and communicate it so risk decisions are consistent.',
    example: 'The board approves a risk appetite statement: "We accept low risk on customer PII, moderate risk on internal tools, and tolerate higher risk on non-customer-facing innovation projects."',
    evidence_tips: [
      'Board or executive-approved risk appetite statement',
      'Risk tolerance thresholds for different data or system categories',
      'Evidence that risk appetite is referenced in risk acceptance decisions',
    ],
    common_mistakes: 'Treating risk appetite as an abstract concept rather than operationalizing it into measurable thresholds that guide actual decisions.',
    nist_reference: 'GV.RM-02',
  },
  'GV.RM-03': {
    plain_english: 'Maintain a risk register that catalogs, categorizes, and prioritizes cybersecurity risks across the organization.',
    example: 'The security team maintains a risk register in Jira with 45 open items, each tagged by severity, business owner, treatment plan, and due date. The top 10 are reviewed monthly by the CISO.',
    evidence_tips: [
      'Active risk register with entries for identified risks',
      'Risk prioritization methodology (likelihood × impact matrix)',
      'Regular review records showing the register is maintained',
    ],
    common_mistakes: 'Creating a risk register for an audit, then never updating it — risks get stale and the register loses credibility as a decision-making tool.',
    nist_reference: 'GV.RM-03',
  },
  'GV.RM-04': {
    plain_english: 'Cybersecurity risk management should be integrated with your broader enterprise risk program, not a separate isolated function.',
    example: 'The Chief Risk Officer\'s quarterly board report includes cyber risks alongside financial, operational, and reputational risks, all using the same scoring methodology.',
    evidence_tips: [
      'Enterprise risk management framework that includes cybersecurity',
      'Joint risk committee meeting minutes with cyber and enterprise risk topics',
      'Consistent risk scoring methodology across cyber and other risk domains',
    ],
    common_mistakes: 'Security runs its own risk program using different terminology and scoring, making it impossible for leadership to compare cyber risk against other business risks.',
    nist_reference: 'GV.RM-04',
  },
  'GV.RM-05': {
    plain_english: 'Define who is responsible for cybersecurity risk decisions at the strategic, operational, and tactical levels.',
    example: 'The CISO owns strategic risk decisions, system owners own operational risk acceptance, and team leads handle tactical control implementation with defined escalation paths.',
    evidence_tips: [
      'RACI chart for cybersecurity risk management activities',
      'Documented risk acceptance authority levels by role',
      'Evidence that risk decisions are made by the designated authority',
    ],
    common_mistakes: 'Risk acceptance decisions made by whoever raises the ticket, rather than by someone with designated authority and accountability.',
    nist_reference: 'GV.RM-05',
  },
  'GV.RM-06': {
    plain_english: 'Use risk assessment results to drive where you invest in security — don\'t just do assessments as a compliance exercise.',
    example: 'After a risk assessment reveals high exposure in cloud misconfigurations, the security budget is reallocated to deploy a CSPM tool rather than expanding an already-mature perimeter firewall.',
    evidence_tips: [
      'Risk assessment findings linked to budget requests or project priorities',
      'Security roadmap that references risk assessment results',
      'Documentation showing risk-driven prioritization of security investments',
    ],
    common_mistakes: 'Security investments are driven by vendor relationships or historical precedent rather than current risk assessment findings.',
    nist_reference: 'GV.RM-06',
  },
  'GV.RM-07': {
    plain_english: 'Senior leadership — including the board — must regularly review and approve the cybersecurity risk management strategy and the status of significant risks.',
    example: 'The board receives a quarterly cybersecurity risk briefing, reviews the top 5 risks, approves or modifies the risk tolerance statement, and this is documented in board minutes.',
    evidence_tips: [
      'Board or executive committee meeting minutes showing risk reviews',
      'Cybersecurity risk briefing materials presented to leadership',
      'Evidence of leadership decisions on risk acceptance or treatment',
    ],
    common_mistakes: 'Security leadership briefs the board on incidents but never presents ongoing risk management strategy reviews for formal approval.',
    nist_reference: 'GV.RM-07',
  },

  // ── GOVERN — Roles, Responsibilities, and Authorities ─────────────────────────
  'GV.RR-01': {
    plain_english: 'Define, assign, and communicate cybersecurity roles and responsibilities so everyone knows who is accountable for what.',
    example: 'The security policy defines roles from the CISO to system administrator, each with specific responsibilities. New employees receive role-specific security responsibility briefings during onboarding.',
    evidence_tips: [
      'Cybersecurity RACI matrix or role responsibility document',
      'Job descriptions referencing security responsibilities',
      'Onboarding records showing security role briefings',
    ],
    common_mistakes: 'Assigning "everyone is responsible for security" without defining specific accountabilities — a guarantee that critical tasks fall through the cracks.',
    nist_reference: 'GV.RR-01',
  },
  'GV.RR-02': {
    plain_english: 'Leadership must be formally accountable for cybersecurity risk decisions, not just supportive of them.',
    example: 'The CEO signs the organization\'s information security policy annually. The CISO reports to the CEO with dotted-line reporting to the audit committee, ensuring executive accountability.',
    evidence_tips: [
      'Signed security policy with CEO or board-level sign-off',
      'Executive accountability matrix for cybersecurity risk decisions',
      'Documented escalation process that reaches executive level',
    ],
    common_mistakes: 'Security policies signed by IT leadership only — cybersecurity accountability stops at the CISO rather than flowing up to the CEO or board.',
    nist_reference: 'GV.RR-02',
  },
  'GV.RR-03': {
    plain_english: 'Provide adequate budget, staff, tools, and time to achieve your cybersecurity objectives.',
    example: 'The annual security budget is explicitly tied to the risk management strategy. Headcount requests are justified by risk assessments, not just workload estimates.',
    evidence_tips: [
      'Security budget documentation showing alignment to risk objectives',
      'Headcount planning documents referencing security staffing needs',
      'Resource requests rejected or approved with documented rationale',
    ],
    common_mistakes: 'Security teams are chronically underfunded, yet leadership approves risk acceptance without acknowledging that insufficient resources caused the gap.',
    nist_reference: 'GV.RR-03',
  },
  'GV.RR-04': {
    plain_english: 'Put cybersecurity responsibilities into job descriptions and performance plans so people are actually held accountable.',
    example: 'Every employee\'s annual performance review includes a security awareness score. System owner job descriptions include maintaining the security posture of their assigned systems.',
    evidence_tips: [
      'Job descriptions with specific cybersecurity responsibilities',
      'Performance review templates including security accountability metrics',
      'Annual review records showing security performance was evaluated',
    ],
    common_mistakes: 'Cybersecurity responsibilities appear in policies but not in job descriptions or performance evaluations, so there is no real accountability.',
    nist_reference: 'GV.RR-04',
  },

  // ── GOVERN — Policy ────────────────────────────────────────────────────────────
  'GV.PO-01': {
    plain_english: 'Have documented, leadership-approved cybersecurity policies that reflect your risk environment, obligations, and current threats.',
    example: 'The CISO maintains a policy library covering access control, incident response, data classification, and acceptable use. Each policy is signed by the CEO and linked to relevant regulations.',
    evidence_tips: [
      'Policy library with executive approval signatures and effective dates',
      'Policies mapped to applicable regulations and risk requirements',
      'Evidence policies are communicated and accessible to all employees',
    ],
    common_mistakes: 'Policies that were written years ago for a different threat environment and never updated — still listing fax machines as restricted devices.',
    nist_reference: 'GV.PO-01',
  },
  'GV.PO-02': {
    plain_english: 'Review and update cybersecurity policies regularly to keep pace with changes in threats, regulations, and your organization.',
    example: 'Policies are reviewed annually on a rolling schedule. A trigger-based review is required after any major incident, regulatory change, or significant technology change.',
    evidence_tips: [
      'Policy review calendar showing scheduled and completed reviews',
      'Revision history in each policy document',
      'Change log linking policy updates to triggering events',
    ],
    common_mistakes: 'Annual review is on the calendar but consistently deprioritized — policies accumulate years without meaningful updates.',
    nist_reference: 'GV.PO-02',
  },

  // ── GOVERN — Oversight ─────────────────────────────────────────────────────────
  'GV.OV-01': {
    plain_english: 'Leadership must actively evaluate whether the cybersecurity risk strategy is working and aligned with business needs.',
    example: 'The board\'s audit committee receives a semi-annual cybersecurity program effectiveness report, including KPIs, open risks, and a self-assessment against the CSF maturity targets.',
    evidence_tips: [
      'Board/audit committee meeting minutes showing cybersecurity program reviews',
      'Cybersecurity program effectiveness reports with KPIs',
      'Evidence of leadership decisions based on program review findings',
    ],
    common_mistakes: 'Leadership receives only incident reports, not ongoing program effectiveness metrics — so they only engage with security during crises.',
    nist_reference: 'GV.OV-01',
  },
  'GV.OV-02': {
    plain_english: 'Define and track metrics and KPIs to monitor whether your cybersecurity program is performing as expected.',
    example: 'Monthly metrics include: mean time to patch critical vulnerabilities, phishing simulation click rates, percentage of systems with current backups, and open critical findings.',
    evidence_tips: [
      'Defined set of cybersecurity KPIs and KRIs with targets',
      'Regular metric reports showing current performance vs. targets',
      'Dashboard or reporting tool used to track and communicate metrics',
    ],
    common_mistakes: 'Measuring activity (number of patches applied) rather than outcomes (percentage of critical vulnerabilities remediated within SLA).',
    nist_reference: 'GV.OV-02',
  },
  'GV.OV-03': {
    plain_english: 'Use assessment findings, audit results, and monitoring data to continuously improve your cybersecurity program.',
    example: 'After an external audit identifies five policy gaps, the CISO creates a remediation plan with owners and due dates, tracked monthly until closure.',
    evidence_tips: [
      'Remediation plans linked to audit or assessment findings',
      'Tracking records showing findings through to closure',
      'Evidence that lessons learned influenced policy or process updates',
    ],
    common_mistakes: 'Audit findings are documented and then filed away — there is no tracking process to ensure remediation actually happens.',
    nist_reference: 'GV.OV-03',
  },

  // ── GOVERN — Supply Chain Risk Management ─────────────────────────────────────
  'GV.SC-01': {
    plain_english: 'Have a documented program for managing cybersecurity risks introduced by your suppliers, vendors, and service providers.',
    example: 'The procurement team uses a vendor risk management platform to track all third-party vendors, their risk tiers, last assessment date, and outstanding issues.',
    evidence_tips: [
      'Vendor risk management policy or program document',
      'Vendor inventory with risk tier assignments',
      'Supply chain risk management plan reviewed annually',
    ],
    common_mistakes: 'Treating supply chain risk as a one-time due diligence exercise during vendor onboarding rather than an ongoing management program.',
    nist_reference: 'GV.SC-01',
  },
  'GV.SC-02': {
    plain_english: 'Identify your most critical suppliers and prioritize risk management effort based on the potential impact if they were compromised.',
    example: 'The organization classifies vendors as Tier 1 (critical), Tier 2 (significant), or Tier 3 (low risk) based on data access, system integration depth, and business dependency.',
    evidence_tips: [
      'Vendor criticality assessment methodology and results',
      'Tiered vendor list with criticality rationale documented',
      'Evidence that higher-tier vendors receive more intensive oversight',
    ],
    common_mistakes: 'Applying the same minimal due diligence to a critical cloud provider as to a low-risk office supply vendor.',
    nist_reference: 'GV.SC-02',
  },
  'GV.SC-03': {
    plain_english: 'Include specific cybersecurity requirements and incident notification obligations in supplier contracts.',
    example: 'All vendor contracts include clauses requiring: encryption of shared data, breach notification within 48 hours, annual security assessments, and right-to-audit provisions.',
    evidence_tips: [
      'Standard contract language library with cybersecurity requirements',
      'Sample vendor contracts showing security clauses',
      'Contract review checklist ensuring security requirements are included',
    ],
    common_mistakes: 'Security requirements are in the SLA but not the actual contract, making them unenforceable.',
    nist_reference: 'GV.SC-03',
  },
  'GV.SC-04': {
    plain_english: 'Assess the origin, integrity, and trustworthiness of software, hardware, and services before deploying them.',
    example: 'Before deploying any open-source library, developers run a software composition analysis scan and verify the package against known vulnerability databases and provenance data.',
    evidence_tips: [
      'Software composition analysis (SCA) tool outputs for key systems',
      'Hardware procurement process with authenticity verification steps',
      'Policy requiring security review before new components are deployed',
    ],
    common_mistakes: 'Deploying third-party software without verifying its provenance, assuming that popular packages are inherently trustworthy.',
    nist_reference: 'GV.SC-04',
  },
  'GV.SC-05': {
    plain_english: 'Have a plan for responding to cybersecurity incidents that involve your suppliers or supply chain disruptions.',
    example: 'The incident response plan has a specific playbook for "third-party breach affecting our environment," including steps to isolate integration points and notify customers.',
    evidence_tips: [
      'Incident response plan with supply chain incident scenarios',
      'Supply chain incident playbook or runbook',
      'Contact list for critical suppliers\' security incident contacts',
    ],
    common_mistakes: 'Incident response plans only cover internal incidents — when a critical SaaS provider is breached, there is no playbook for what to do.',
    nist_reference: 'GV.SC-05',
  },
  'GV.SC-06': {
    plain_english: 'Verify that your suppliers are actually meeting their cybersecurity contractual obligations through audits, assessments, or ongoing monitoring.',
    example: 'Tier 1 vendors complete an annual security questionnaire; critical vendors with system access undergo a third-party penetration test every two years. Results are reviewed by the security team.',
    evidence_tips: [
      'Vendor assessment records (questionnaires, audit reports, certifications)',
      'Monitoring program for ongoing compliance verification',
      'Records of remediation requests sent to non-compliant vendors',
    ],
    common_mistakes: 'Asking vendors to complete questionnaires but never following up on red flags or verifying the accuracy of self-reported answers.',
    nist_reference: 'GV.SC-06',
  },
  'GV.SC-07': {
    plain_english: 'Manage supply chain cybersecurity risks across the entire lifecycle — from selecting a vendor through decommissioning their services.',
    example: 'The vendor lifecycle process includes: security review at onboarding, annual assessments during the relationship, and a secure offboarding checklist covering credential revocation and data return.',
    evidence_tips: [
      'Vendor lifecycle management process documentation',
      'Offboarding checklist evidence showing credential and access termination',
      'Lifecycle stage records for critical vendors',
    ],
    common_mistakes: 'Performing due diligence at vendor onboarding but never reviewing security posture during the relationship and skipping secure offboarding.',
    nist_reference: 'GV.SC-07',
  },
  'GV.SC-08': {
    plain_english: 'Evaluate risks from components of unknown or uncertain origin — including open-source software — before deploying them.',
    example: 'The development team uses a SBOM (software bill of materials) for all applications. Before merging a dependency, automated checks verify there are no known vulnerabilities or license issues.',
    evidence_tips: [
      'Software bill of materials (SBOM) for key applications',
      'Open-source risk scanning tool (Snyk, FOSSA, etc.) results',
      'Policy requiring provenance review for third-party components',
    ],
    common_mistakes: 'Developers freely pull open-source packages without any vetting process, creating hidden dependencies on unmaintained or compromised components.',
    nist_reference: 'GV.SC-08',
  },
  'GV.SC-09': {
    plain_english: 'Assess the cybersecurity posture of critical suppliers through assessments, audits, certifications, or other evaluation mechanisms.',
    example: 'Critical cloud infrastructure providers must submit SOC 2 Type II reports annually. For vendors without third-party attestations, the security team conducts a direct assessment.',
    evidence_tips: [
      'SOC 2 or ISO 27001 certificates collected from critical vendors',
      'Custom security questionnaire responses for non-certified vendors',
      'Assessment schedule showing when each critical vendor was last evaluated',
    ],
    common_mistakes: 'Accepting a vendor\'s SOC 2 report without verifying it covers the services you actually use or that the scope matches your use case.',
    nist_reference: 'GV.SC-09',
  },
  'GV.SC-10': {
    plain_english: 'Implement controls to reduce the risk of receiving counterfeit, fraudulent, or compromised hardware and software through the supply chain.',
    example: 'Network equipment is purchased only from authorized resellers. Upon receipt, serial numbers are verified against manufacturer databases before installation.',
    evidence_tips: [
      'Approved hardware vendor list with authorized reseller documentation',
      'Hardware receipt and verification process records',
      'Counterfeit component detection procedures',
    ],
    common_mistakes: 'Purchasing hardware from the cheapest online marketplace without verifying authenticity, risking counterfeit or pre-compromised components.',
    nist_reference: 'GV.SC-10',
  },

  // ── IDENTIFY — Asset Management ────────────────────────────────────────────────
  'ID.AM-01': {
    plain_english: 'Maintain an accurate, current inventory of all hardware assets connected to or used in your operations.',
    example: 'An IT asset management platform auto-discovers all devices on the network. Inventory is reconciled against procurement records quarterly, and unmanaged devices are flagged for investigation.',
    evidence_tips: [
      'Asset inventory from CMDB, IT asset management, or discovery tool',
      'Last-updated date and reconciliation records',
      'Process for adding new hardware to inventory',
    ],
    common_mistakes: 'Maintaining a hardware inventory that is only updated when IT has time — shadow IT devices, contractor laptops, and IoT equipment are routinely missed.',
    nist_reference: 'ID.AM-01',
  },
  'ID.AM-02': {
    plain_english: 'Maintain a current inventory of all software, applications, cloud services, and data repositories authorized for use.',
    example: 'Software inventory is maintained in the CMDB and includes version numbers, license status, and data sensitivity. Cloud service discovery tools identify unauthorized SaaS usage monthly.',
    evidence_tips: [
      'Software asset inventory with version and license information',
      'Cloud access security broker (CASB) or shadow IT discovery results',
      'Process for approving and adding new software to inventory',
    ],
    common_mistakes: 'Tracking software licenses but not cloud services — employees signing up for dozens of SaaS tools with organizational data, none of which are in the inventory.',
    nist_reference: 'ID.AM-02',
  },
  'ID.AM-03': {
    plain_english: 'Map and document your network topology, data flows, and how systems connect to each other and to external networks.',
    example: 'A current network diagram shows all VLANs, firewall boundaries, cloud connections, and data flows between key systems. It is updated after any significant network change.',
    evidence_tips: [
      'Current network architecture diagram with last-updated date',
      'Data flow diagrams for systems handling sensitive data',
      'Process for updating diagrams when the environment changes',
    ],
    common_mistakes: 'Network diagrams are created for compliance and then never updated — after 18 months of change, they bear no resemblance to the actual environment.',
    nist_reference: 'ID.AM-03',
  },
  'ID.AM-04': {
    plain_english: 'Identify and document external systems — cloud platforms, SaaS, managed services — that store, process, or transmit your organization\'s data.',
    example: 'The vendor inventory includes all SaaS applications processing customer data, what data each stores, and the data classification level. Cloud accounts are documented with their access model.',
    evidence_tips: [
      'External system inventory with data types processed by each',
      'Cloud account inventory with data sensitivity classifications',
      'Process for reviewing new external system approvals',
    ],
    common_mistakes: 'Knowing about major SaaS providers but missing dozens of departmental tools that employees signed up for without IT involvement.',
    nist_reference: 'ID.AM-04',
  },
  'ID.AM-05': {
    plain_english: 'Prioritize your assets based on their criticality, sensitivity, and business value so you protect the most important things first.',
    example: 'Assets are classified as Critical, High, Medium, or Low based on data sensitivity and business impact. Customer database servers are Critical; employee laptops are High; test servers are Medium.',
    evidence_tips: [
      'Asset classification or criticality scoring documentation',
      'Evidence that critical assets receive enhanced protection',
      'Classification criteria used to determine asset priority',
    ],
    common_mistakes: 'Treating all assets equally — spending the same protection effort on a test VM as on the production database containing all customer PII.',
    nist_reference: 'ID.AM-05',
  },
  'ID.AM-06': {
    plain_english: 'Know where all your sensitive data is — where it is created, stored, processed, and transmitted, inside and outside your organization.',
    example: 'A data map documents that customer PII flows from the web app to a PostgreSQL database, is backed up to S3, and is shared with three third-party analytics vendors.',
    evidence_tips: [
      'Data inventory or data map showing sensitive data locations',
      'Data flow diagrams for regulated data categories',
      'Data discovery tool results identifying sensitive data across systems',
    ],
    common_mistakes: 'Documenting data flows in theory without validating where data actually goes — sensitive data routinely appears in unexpected systems like log files or BI tools.',
    nist_reference: 'ID.AM-06',
  },
  'ID.AM-07': {
    plain_english: 'Manage data and information assets throughout their lifecycle — from creation through classification, handling, and authorized use.',
    example: 'The data lifecycle policy defines classification at creation, required handling controls per classification, retention periods, and secure disposal methods.',
    evidence_tips: [
      'Data lifecycle management policy',
      'Data classification scheme with handling requirements',
      'Evidence of data classification in practice (labeled files, classified systems)',
    ],
    common_mistakes: 'Classifying data at rest but not managing how it is handled throughout its lifecycle — sensitive data ends up in unprotected backups or shared drives.',
    nist_reference: 'ID.AM-07',
  },
  'ID.AM-08': {
    plain_english: 'Manage systems and technology assets from planning and acquisition through secure decommissioning and disposal.',
    example: 'The system lifecycle process includes security requirements in procurement, hardening before deployment, patch management during operation, and DoD-compliant wiping before disposal.',
    evidence_tips: [
      'System lifecycle management process documentation',
      'Decommissioning records showing secure disposal of retired systems',
      'Procurement security requirements checklist',
    ],
    common_mistakes: 'Retiring systems by simply disconnecting them — hard drives containing sensitive data end up in landfills or are sold containing recoverable organizational data.',
    nist_reference: 'ID.AM-08',
  },

  // ── IDENTIFY — Risk Assessment ─────────────────────────────────────────────────
  'ID.RA-01': {
    plain_english: 'Maintain a current understanding of the threats relevant to your industry, assets, and operations.',
    example: 'The security team subscribes to industry-specific ISACs, US-CERT alerts, and vendor advisories. A monthly threat briefing summarizes relevant threat actors and recent attack patterns.',
    evidence_tips: [
      'Threat intelligence subscription or membership records (ISAC, CISA, etc.)',
      'Threat briefing materials or reports produced for security leadership',
      'Evidence that threat intel feeds into risk assessments',
    ],
    common_mistakes: 'Relying solely on vendor-provided threat intelligence without context about which threats are relevant to your specific industry and asset profile.',
    nist_reference: 'ID.RA-01',
  },
  'ID.RA-02': {
    plain_english: 'Use threat intelligence from trusted external sources to inform your risk decisions.',
    example: 'The organization participates in FS-ISAC and integrates STIX/TAXII threat feeds into its SIEM. New indicators of compromise are automatically added to detection rules.',
    evidence_tips: [
      'Threat intelligence platform or feed subscriptions',
      'Records showing threat intel used in risk assessment decisions',
      'Integration between threat intel and security monitoring tools',
    ],
    common_mistakes: 'Subscribing to threat feeds but not operationalizing them — threat intel sits in a dashboard that nobody regularly reviews or acts on.',
    nist_reference: 'ID.RA-02',
  },
  'ID.RA-03': {
    plain_english: 'Identify and analyze internal and external threats that could exploit weaknesses in your systems.',
    example: 'The annual risk assessment includes a threat analysis covering ransomware, insider threats, DDoS, and supply chain attacks. Each threat is mapped to relevant organizational vulnerabilities.',
    evidence_tips: [
      'Risk assessment reports with threat identification sections',
      'Threat modeling documentation for key systems',
      'Evidence of both internal and external threat consideration',
    ],
    common_mistakes: 'Only considering external threats — insider threat risk, misconfigured systems, and human error are consistently underestimated.',
    nist_reference: 'ID.RA-03',
  },
  'ID.RA-04': {
    plain_english: 'Estimate the potential business impact and likelihood of identified threats to prioritize what to fix first.',
    example: 'Each risk in the register has a likelihood (1-5) and impact (1-5) score with a narrative justification. Risks above a threshold require formal treatment plans approved by the CISO.',
    evidence_tips: [
      'Risk scoring methodology documentation',
      'Risk register with likelihood and impact ratings',
      'Evidence that high-risk items receive priority treatment',
    ],
    common_mistakes: 'Scoring risks without documenting the rationale — risk scores become arbitrary, and the organization cannot defend its prioritization decisions.',
    nist_reference: 'ID.RA-04',
  },
  'ID.RA-05': {
    plain_english: 'Find vulnerabilities in your systems through scanning, testing, and other discovery methods, and track them to remediation.',
    example: 'Weekly automated vulnerability scans run against all in-scope systems. Critical findings are assigned to system owners and tracked to remediation within 30 days. Results are reviewed by the security team.',
    evidence_tips: [
      'Vulnerability scan reports with findings and remediation status',
      'Vulnerability remediation SLAs and tracking records',
      'Penetration test reports and remediation tracking',
    ],
    common_mistakes: 'Running vulnerability scans but not tracking findings to closure — the same critical vulnerabilities appear on scan reports month after month.',
    nist_reference: 'ID.RA-05',
  },
  'ID.RA-06': {
    plain_english: 'Aggregate and analyze risk assessment findings to produce a prioritized risk register that guides security investment.',
    example: 'After quarterly vulnerability scans, risk assessments, and threat intel reviews, findings are consolidated into the master risk register with unified priority scoring.',
    evidence_tips: [
      'Risk aggregation process documentation',
      'Master risk register combining findings from multiple sources',
      'Evidence that the register is used to prioritize security work',
    ],
    common_mistakes: 'Maintaining separate lists of vulnerabilities, audit findings, and risk items that never get consolidated — leadership has no single view of total risk.',
    nist_reference: 'ID.RA-06',
  },
  'ID.RA-07': {
    plain_english: 'Conduct risk assessments that account for your risk appetite, business objectives, and applicable regulatory requirements.',
    example: 'The risk assessment methodology explicitly references the organization\'s risk appetite statement and maps findings to regulatory requirements (PCI DSS, HIPAA), not just technical severity.',
    evidence_tips: [
      'Risk assessment methodology that references risk appetite',
      'Risk assessments that map findings to regulatory requirements',
      'Evidence of business context in risk assessment reports',
    ],
    common_mistakes: 'Risk assessments focus purely on technical severity (CVSS scores) without considering business context — a critical CVSS finding on an isolated test system gets the same treatment as one on a customer-facing system.',
    nist_reference: 'ID.RA-07',
  },
  'ID.RA-08': {
    plain_english: 'Have a documented process for receiving, evaluating, and responding to vulnerability disclosures from external researchers, vendors, or customers.',
    example: 'The organization publishes a security.txt file and a responsible disclosure policy. Reported vulnerabilities are triaged within 48 hours, assigned severity, and remediated with the reporter notified.',
    evidence_tips: [
      'Published vulnerability disclosure policy',
      'Bug bounty program or responsible disclosure process documentation',
      'Records of received disclosures and their resolution',
    ],
    common_mistakes: 'Having no defined process for receiving vulnerability reports — researchers cannot find a contact, and reported vulnerabilities go unacknowledged.',
    nist_reference: 'ID.RA-08',
  },
  'ID.RA-09': {
    plain_english: 'Assess the authenticity, integrity, and security of hardware and software before deploying them, including supply chain risks.',
    example: 'Before deploying a new software library, developers verify checksums, check the project\'s maintenance status, and run SAST scans. Hardware serial numbers are verified with the manufacturer.',
    evidence_tips: [
      'Pre-deployment security review checklist for software and hardware',
      'Checksum verification records for critical software deployments',
      'Software composition analysis results for key applications',
    ],
    common_mistakes: 'Deploying software downloaded from the internet without verifying integrity — even official packages have been compromised in supply chain attacks.',
    nist_reference: 'ID.RA-09',
  },
  'ID.RA-10': {
    plain_english: 'Assess cybersecurity risks from critical suppliers and supply chain components to understand potential cascading impacts.',
    example: 'The organization identifies that a breach of its payroll SaaS provider would expose all employee PII and banking data. This is documented in the risk register with a specific treatment plan.',
    evidence_tips: [
      'Supply chain risk assessments for critical vendors',
      'Dependency mapping showing potential cascading impact scenarios',
      'Risk register entries for supply chain risks',
    ],
    common_mistakes: 'Assessing vendor security in isolation without considering how a vendor breach or outage would cascade into your own operations.',
    nist_reference: 'ID.RA-10',
  },

  // ── IDENTIFY — Improvement ─────────────────────────────────────────────────────
  'ID.IM-01': {
    plain_english: 'Use findings from assessments, audits, and lessons learned to continuously improve your cybersecurity practices.',
    example: 'After each annual audit, the CISO creates a formal improvement plan with owners and due dates. Progress is reviewed quarterly by the security leadership team.',
    evidence_tips: [
      'Improvement plans linked to assessment or audit findings',
      'Tracking records showing improvements through to completion',
      'Evidence that improvements reduced identified risks',
    ],
    common_mistakes: 'Completing assessments and generating reports but treating them as deliverables rather than starting points for improvement.',
    nist_reference: 'ID.IM-01',
  },
  'ID.IM-02': {
    plain_english: 'Learn from security incidents and near misses by incorporating those lessons into updated plans and procedures.',
    example: 'Every significant incident results in a post-incident review. Key lessons are formally documented, assigned to owners, and the relevant policy or procedure is updated before the next review cycle.',
    evidence_tips: [
      'Post-incident review reports with lessons learned sections',
      'Policy or procedure update records triggered by incidents',
      'Lessons-learned tracking from incident to process improvement',
    ],
    common_mistakes: 'Conducting post-incident reviews but filing the reports — the same type of incident recurs because nobody updated the response procedures.',
    nist_reference: 'ID.IM-02',
  },
  'ID.IM-03': {
    plain_english: 'Use exercises and drills to test your security capabilities and find gaps that need improvement.',
    example: 'Annual tabletop exercises simulate a ransomware attack. Gaps identified (no offline backups, unclear notification chain) are tracked as action items and verified closed before the next exercise.',
    evidence_tips: [
      'Exercise and drill schedules and completion records',
      'After-action reports documenting identified gaps',
      'Action item tracking from exercise findings to resolution',
    ],
    common_mistakes: 'Running the same tabletop exercise scenario every year without varying the scenario or tracking whether last year\'s gaps were actually resolved.',
    nist_reference: 'ID.IM-03',
  },
  'ID.IM-04': {
    plain_english: 'Share cybersecurity insights and lessons learned with industry peers and relevant stakeholders.',
    example: 'The security team participates in quarterly ISAC threat briefings and shares sanitized indicators from incidents with the sector ISAC. Lessons learned are presented at industry conferences.',
    evidence_tips: [
      'ISAC membership and participation records',
      'Records of threat intelligence shared with external organizations',
      'Conference presentations or industry collaboration documentation',
    ],
    common_mistakes: 'Consuming threat intelligence from community sources without contributing back — free-riding on community resources without giving value in return.',
    nist_reference: 'ID.IM-04',
  },

  // ── PROTECT — Identity Management, Authentication, and Access Control ──────────
  'PR.AA-01': {
    plain_english: 'Manage identities and credentials for all authorized users, services, and devices throughout their lifecycle — from issuance through revocation.',
    example: 'An identity governance platform manages the full lifecycle: provisioning accounts on hire, reviewing access quarterly, and automatically revoking access within 24 hours of termination.',
    evidence_tips: [
      'Identity management platform records (Active Directory, Okta, etc.)',
      'User provisioning and deprovisioning process documentation',
      'Access review records showing quarterly certification',
    ],
    common_mistakes: 'Provisioning access quickly but being slow to revoke it — former employees retain system access for weeks after termination.',
    nist_reference: 'PR.AA-01',
  },
  'PR.AA-02': {
    plain_english: 'Manage and control physical access to facilities, equipment, and assets based on assessed risk.',
    example: 'Badge access logs are reviewed monthly. Server rooms require dual-factor physical authentication (badge + PIN). Visitor access is logged and all visitors are escorted.',
    evidence_tips: [
      'Physical access control system records (badge logs, access lists)',
      'Visitor log records with escort documentation',
      'Access review records for physical access rights',
    ],
    common_mistakes: 'Installing access control systems but never reviewing the logs — unauthorized physical access goes undetected because nobody looks at the data.',
    nist_reference: 'PR.AA-02',
  },
  'PR.AA-03': {
    plain_english: 'Use authentication mechanisms — including MFA for high-risk access — proportional to the sensitivity of what is being accessed.',
    example: 'MFA is required for all remote access, admin consoles, and cloud management interfaces. Production database access requires hardware MFA tokens. Standard internal tools use SSO with password.',
    evidence_tips: [
      'MFA enrollment and enforcement records',
      'Authentication policy defining MFA requirements by system sensitivity',
      'Configuration evidence showing MFA is enforced on target systems',
    ],
    common_mistakes: 'Enabling MFA for one system as a compliance check while dozens of other sensitive systems remain password-only.',
    nist_reference: 'PR.AA-03',
  },
  'PR.AA-04': {
    plain_english: 'Manage authentication credentials to ensure only authenticated and authorized entities can access your systems.',
    example: 'Service accounts use rotating credentials managed in a secrets manager. Human accounts use SSO with centralized credential management. Default credentials are changed before systems go live.',
    evidence_tips: [
      'Secrets management platform records (HashiCorp Vault, AWS Secrets Manager)',
      'SSO configuration evidence showing centralized authentication',
      'Policy prohibiting default or shared credentials',
    ],
    common_mistakes: 'Hardcoding service account credentials in configuration files or source code — a common cause of breaches when repositories are exposed.',
    nist_reference: 'PR.AA-04',
  },
  'PR.AA-05': {
    plain_english: 'Grant access based on least privilege and need-to-know, and regularly review and revoke unnecessary permissions.',
    example: 'Quarterly access reviews require managers to certify each employee\'s system access. Access not certified is automatically removed. Privileged accounts require separate approval with business justification.',
    evidence_tips: [
      'Access review records showing quarterly certification',
      'Least privilege policy and configuration evidence',
      'Privileged access management (PAM) tool records',
    ],
    common_mistakes: 'Granting broad access on hire because it is easier, then never reviewing whether employees still need that access as their role evolves.',
    nist_reference: 'PR.AA-05',
  },
  'PR.AA-06': {
    plain_english: 'Implement physical access controls that restrict entry to critical system areas and maintain records of physical access events.',
    example: 'The data center requires badge access to enter. All entry attempts are logged. Security cameras cover server aisles. Quarterly reviews compare badge access logs against authorized user lists.',
    evidence_tips: [
      'Physical access control logs from server rooms and data centers',
      'Camera footage retention policy and access records',
      'Physical access review records',
    ],
    common_mistakes: 'Installing physical access controls but not reviewing the logs — the goal is detection, not just restriction.',
    nist_reference: 'PR.AA-06',
  },

  // ── PROTECT — Awareness and Training ──────────────────────────────────────────
  'PR.AT-01': {
    plain_english: 'Provide cybersecurity awareness training to all personnel, covering their responsibilities and the threats relevant to your organization.',
    example: 'All employees complete annual security awareness training covering phishing, data handling, and incident reporting. Phishing simulations are run monthly. New hires train before system access is granted.',
    evidence_tips: [
      'Training completion records for all employees',
      'Security awareness training program content and curriculum',
      'Phishing simulation results and remedial training records',
    ],
    common_mistakes: 'Annual compliance training with no reinforcement — employees click through once a year, retain nothing, and remain susceptible to phishing.',
    nist_reference: 'PR.AT-01',
  },
  'PR.AT-02': {
    plain_english: 'Ensure people in cybersecurity-specific roles have the specialized training and skills required for their responsibilities.',
    example: 'SOC analysts complete SANS SEC555 before joining the team. Security engineers hold cloud security certifications. Training budgets are allocated per role based on skill requirements.',
    evidence_tips: [
      'Role-specific training plans and completion records',
      'Certification records for security staff (CISSP, CISM, etc.)',
      'Job descriptions with required training or certification',
    ],
    common_mistakes: 'Expecting security staff to stay current through on-the-job learning without providing budget or time for formal training and certifications.',
    nist_reference: 'PR.AT-02',
  },

  // ── PROTECT — Data Security ────────────────────────────────────────────────────
  'PR.DS-01': {
    plain_english: 'Protect data at rest using encryption, access controls, and integrity verification proportional to the data\'s sensitivity.',
    example: 'Customer PII databases use AES-256 encryption at rest with keys managed in HSMs. Encryption status is verified through quarterly configuration audits.',
    evidence_tips: [
      'Encryption configuration records for databases and storage',
      'Key management policy and HSM configuration evidence',
      'Configuration audit results confirming encryption at rest',
    ],
    common_mistakes: 'Encrypting data at rest as a checkbox without managing the encryption keys — poor key management effectively defeats the encryption.',
    nist_reference: 'PR.DS-01',
  },
  'PR.DS-02': {
    plain_english: 'Protect data in transit using cryptographic controls to prevent unauthorized interception or modification.',
    example: 'All web applications use TLS 1.2 or higher. APIs use mutual TLS for service-to-service communication. Certificate configuration is scanned monthly and weak ciphers are blocked.',
    evidence_tips: [
      'TLS configuration scan results (SSL Labs, testssl.sh)',
      'Network traffic encryption policy',
      'Certificate management records',
    ],
    common_mistakes: 'Enabling HTTPS on the web frontend while leaving internal service-to-service communication unencrypted on the assumption that the internal network is trusted.',
    nist_reference: 'PR.DS-02',
  },
  'PR.DS-03': {
    plain_english: 'Manage data throughout its full lifecycle including creation, use, retention, archiving, and secure disposal.',
    example: 'The data retention policy specifies retention periods by data type. Data older than the retention period is automatically purged. Media containing sensitive data is wiped before disposal.',
    evidence_tips: [
      'Data retention policy with retention periods by data type',
      'Automated data deletion records or scheduled purge evidence',
      'Media sanitization and disposal records',
    ],
    common_mistakes: 'Retaining data indefinitely "just in case" — organizations that keep everything forever create massive breach exposure with no countervailing business value.',
    nist_reference: 'PR.DS-03',
  },
  'PR.DS-04': {
    plain_english: 'Maintain adequate capacity and redundancy in processing and storage systems to meet your availability requirements.',
    example: 'Critical services are deployed across multiple availability zones with auto-scaling. Capacity planning reviews are conducted quarterly. Recovery time objectives are tested annually.',
    evidence_tips: [
      'Infrastructure capacity planning documentation',
      'High availability architecture diagrams',
      'Availability SLA records and performance against them',
    ],
    common_mistakes: 'Designing for average load without capacity planning for peak demand or failover scenarios, leading to outages during high-traffic events.',
    nist_reference: 'PR.DS-04',
  },
  'PR.DS-05': {
    plain_english: 'Implement data loss prevention (DLP) or equivalent controls to detect and prevent unauthorized exfiltration of sensitive data.',
    example: 'Email DLP blocks outbound messages containing PAN or SSN patterns. Endpoint DLP prevents copying sensitive files to USB drives. Cloud DLP scans shared drives for unauthorized PII.',
    evidence_tips: [
      'DLP policy configurations and enforcement records',
      'DLP alert and incident logs',
      'Evidence of DLP coverage across key data channels (email, cloud, endpoint)',
    ],
    common_mistakes: 'Deploying DLP in monitor-only mode for so long that it effectively becomes decorative — never escalating to block mode because of false positive concerns.',
    nist_reference: 'PR.DS-05',
  },
  'PR.DS-06': {
    plain_english: 'Verify the integrity of software, firmware, and key data using cryptographic mechanisms to detect unauthorized modifications.',
    example: 'Deployment pipelines verify SHA-256 hashes of all software artifacts before installation. File integrity monitoring (FIM) alerts on unauthorized changes to system binaries and configuration files.',
    evidence_tips: [
      'File integrity monitoring tool configuration and alerts',
      'Software deployment hash verification records',
      'Code signing policy and implementation evidence',
    ],
    common_mistakes: 'Relying on perimeter security to detect malicious modifications rather than implementing integrity verification at the file and software level.',
    nist_reference: 'PR.DS-06',
  },
  'PR.DS-07': {
    plain_english: 'Maintain separate development, testing, and production environments with controls preventing unauthorized access or accidental changes to production data.',
    example: 'Production, staging, and development are separate accounts with no shared credentials. Production data cannot be copied to lower environments without explicit approval and data masking.',
    evidence_tips: [
      'Environment separation documentation (separate accounts, VPCs, etc.)',
      'Policy prohibiting production data in non-production environments',
      'Access controls showing different user populations per environment',
    ],
    common_mistakes: 'Developers using production credentials to debug issues — this creates direct paths for accidental data exposure and unauthorized changes.',
    nist_reference: 'PR.DS-07',
  },
  'PR.DS-08': {
    plain_english: 'Use hardware integrity verification mechanisms to detect unauthorized modifications to hardware or firmware.',
    example: 'Servers use UEFI Secure Boot. TPM chips verify boot integrity before OS loading. Hardware attestation is used to confirm device identity before granting network access.',
    evidence_tips: [
      'Secure boot configuration documentation',
      'TPM or hardware attestation implementation records',
      'BIOS/UEFI hardening configuration evidence',
    ],
    common_mistakes: 'Focusing entirely on software security while ignoring firmware — malware that persists in firmware survives OS reinstallation and is extremely difficult to detect.',
    nist_reference: 'PR.DS-08',
  },
  'PR.DS-09': {
    plain_english: 'Implement controls to protect against unauthorized copying, printing, or transmission of sensitive data outside authorized channels.',
    example: 'Screen capture is disabled on systems processing customer data. Printing of sensitive documents is logged. Email encryption is required for sending PII to external parties.',
    evidence_tips: [
      'Screen capture and print restrictions configuration evidence',
      'Email encryption policy and enforcement records',
      'Data exfiltration control configurations',
    ],
    common_mistakes: 'Blocking USB drives while leaving screen capture, email, and cloud storage uploads completely unrestricted — data walks out through other channels.',
    nist_reference: 'PR.DS-09',
  },
  'PR.DS-10': {
    plain_english: 'Protect data in use — including data in memory during processing — from unauthorized access or disclosure.',
    example: 'Memory encryption is enabled on systems processing highly sensitive data. Processes are isolated using containers. Swap memory encryption is enabled to prevent data leakage through paging.',
    evidence_tips: [
      'Memory encryption configuration records',
      'Process isolation architecture documentation',
      'Data-in-use protection controls documentation',
    ],
    common_mistakes: 'Encrypting data at rest and in transit but leaving data in memory unprotected — a common attack vector for credential theft and data scraping.',
    nist_reference: 'PR.DS-10',
  },
  'PR.DS-11': {
    plain_english: 'Maintain verified, tested backups of critical data and regularly test restoration to confirm recoverability.',
    example: 'Automated daily backups run for all production databases. Weekly restoration tests confirm backups are usable. RTO and RPO are documented and verified through quarterly full restore exercises.',
    evidence_tips: [
      'Backup schedule and completion records',
      'Restoration test records with success/failure results',
      'RTO and RPO documentation with test validation evidence',
    ],
    common_mistakes: 'Running backups but never testing restoration — organizations discover their backups are corrupt or unrecoverable only during an actual disaster.',
    nist_reference: 'PR.DS-11',
  },

  // ── PROTECT — Platform Security ────────────────────────────────────────────────
  'PR.PS-01': {
    plain_english: 'Configure hardware, software, and services using secure hardened baseline configurations that remove unnecessary features and reduce attack surface.',
    example: 'CIS Benchmark Level 2 baselines are applied to all servers. New systems are provisioned from hardened AMIs. Compliance is verified by automated configuration scanning weekly.',
    evidence_tips: [
      'Hardening baselines or CIS Benchmark compliance evidence',
      'Automated configuration scanning results',
      'Baseline configuration documentation for key system types',
    ],
    common_mistakes: 'Applying hardening to new systems but not verifying that configurations remain compliant — systems drift from the secure baseline as software is installed and configuration is changed.',
    nist_reference: 'PR.PS-01',
  },
  'PR.PS-02': {
    plain_english: 'Manage the software lifecycle — procurement, development, deployment, patching, and disposal — to reduce cybersecurity risks.',
    example: 'Software is deployed through a CI/CD pipeline with mandatory security scanning. Unsupported software versions trigger automated alerts for upgrade planning. Retired software is removed within 30 days.',
    evidence_tips: [
      'Software lifecycle management policy',
      'End-of-life software inventory and remediation plans',
      'SDLC security controls documentation',
    ],
    common_mistakes: 'Installing software when needed but never planning for end-of-life transitions — the organization ends up running unsupported software that receives no security patches.',
    nist_reference: 'PR.PS-02',
  },
  'PR.PS-03': {
    plain_english: 'Monitor and manage hardware and software configurations to prevent unauthorized changes and drift from secure baselines.',
    example: 'A configuration management tool (Ansible, Chef) enforces desired state. Configuration drift is detected within 4 hours. Change management requires approval before any configuration change.',
    evidence_tips: [
      'Configuration management tool records (Ansible, Puppet, etc.)',
      'Configuration drift detection alerts and remediation records',
      'Change management approval records for configuration changes',
    ],
    common_mistakes: 'Having a change management process for planned changes but allowing ad hoc changes during incidents that are never properly documented or reviewed.',
    nist_reference: 'PR.PS-03',
  },
  'PR.PS-04': {
    plain_english: 'Generate, protect, centralize, and retain event logs to support security monitoring and incident investigation.',
    example: 'All systems forward logs to a centralized SIEM with 13-month retention. Log integrity is protected through write-once storage. Access to raw logs is restricted to the security team.',
    evidence_tips: [
      'SIEM or centralized logging platform records',
      'Log retention configuration showing appropriate retention periods',
      'Log integrity protection configuration evidence',
    ],
    common_mistakes: 'Logging on individual systems without centralizing — when investigating an incident, logs have been overwritten or are spread across dozens of systems that must be accessed individually.',
    nist_reference: 'PR.PS-04',
  },
  'PR.PS-05': {
    plain_english: 'Install and verify security patches and updates for operating systems, applications, and firmware in a timely manner.',
    example: 'Critical patches are deployed within 48 hours of release. High-severity patches deploy within 14 days. A patching dashboard tracks compliance by system. Exceptions require CISO approval.',
    evidence_tips: [
      'Patch management policy with defined SLAs by severity',
      'Patch compliance reports showing current status',
      'Vulnerability scan results showing patching effectiveness',
    ],
    common_mistakes: 'Testing patches so thoroughly before deployment that critical security fixes take months to reach production — the cure is worse than the disease.',
    nist_reference: 'PR.PS-05',
  },
  'PR.PS-06': {
    plain_english: 'Implement secure software development practices — threat modeling, security testing, code review — to reduce vulnerabilities in software you build.',
    example: 'The SDLC requires threat modeling for new features, mandatory static analysis in CI/CD, and security-focused code review for security-sensitive code paths before merging.',
    evidence_tips: [
      'Secure SDLC policy and process documentation',
      'SAST/DAST scan results from CI/CD pipelines',
      'Threat modeling records for key features or systems',
    ],
    common_mistakes: 'Running SAST scans but configuring them to suppress most findings to maintain developer velocity — effectively treating security as a checkbox.',
    nist_reference: 'PR.PS-06',
  },

  // ── DETECT — Continuous Monitoring ────────────────────────────────────────────
  'DE.CM-01': {
    plain_english: 'Continuously monitor your networks for anomalous traffic, unauthorized connections, and indicators of compromise.',
    example: 'A network detection and response (NDR) tool monitors all east-west and north-south traffic. Anomalous connections trigger automated alerts to the SOC within minutes.',
    evidence_tips: [
      'Network monitoring tool configuration and coverage records',
      'SIEM network detection rules and alert records',
      'Network anomaly detection reports',
    ],
    common_mistakes: 'Monitoring only the perimeter while east-west traffic — where attackers move laterally after initial compromise — goes completely unwatched.',
    nist_reference: 'DE.CM-01',
  },
  'DE.CM-02': {
    plain_english: 'Monitor the physical environment of facilities containing critical systems for unauthorized physical access and tampering.',
    example: 'Server rooms use motion-triggered cameras with 90-day retention. Badge access alerts are sent in real time for after-hours access attempts. Security guards conduct hourly facility tours.',
    evidence_tips: [
      'Physical security monitoring system records (cameras, sensors)',
      'After-hours access alert records and response documentation',
      'Physical security audit records',
    ],
    common_mistakes: 'Installing physical monitoring but not reviewing footage or alerts until after an incident — the monitoring value is entirely reactive.',
    nist_reference: 'DE.CM-02',
  },
  'DE.CM-03': {
    plain_english: 'Monitor user and personnel activity on information systems to detect insider threats, policy violations, and account misuse.',
    example: 'User and entity behavior analytics (UEBA) detect anomalous access patterns — unusual download volumes, off-hours access to sensitive data, and access from unexpected locations generate alerts.',
    evidence_tips: [
      'UEBA or user activity monitoring configuration and alerts',
      'Insider threat detection program documentation',
      'User activity log review records',
    ],
    common_mistakes: 'Monitoring perimeter threats while ignoring insider risk — most data breaches involve compromised credentials or malicious insiders who already have legitimate access.',
    nist_reference: 'DE.CM-03',
  },
  'DE.CM-04': {
    plain_english: 'Monitor for malicious software including malware, ransomware, and unauthorized code execution.',
    example: 'Endpoint detection and response (EDR) is deployed on all endpoints with 24/7 monitoring. Malware alerts are triaged by the SOC within 15 minutes. Containment is automated for known ransomware indicators.',
    evidence_tips: [
      'EDR deployment and coverage records',
      'Malware detection alert history and response records',
      'Anti-malware update and signature coverage evidence',
    ],
    common_mistakes: 'Running anti-virus without EDR — traditional AV misses fileless malware, living-off-the-land attacks, and novel malware variants.',
    nist_reference: 'DE.CM-04',
  },
  'DE.CM-05': {
    plain_english: 'Verify and monitor the authenticity and integrity of software and firmware to detect unauthorized modifications.',
    example: 'File integrity monitoring runs on all critical system binaries and configuration files. Changes are alerted to the SOC and require change ticket validation before being approved.',
    evidence_tips: [
      'File integrity monitoring tool deployment and alert records',
      'Software signature verification process documentation',
      'Unauthorized modification alert history',
    ],
    common_mistakes: 'Monitoring files for change without baselining what "good" looks like first — every alert requires manual investigation because you cannot distinguish authorized from unauthorized changes.',
    nist_reference: 'DE.CM-05',
  },
  'DE.CM-06': {
    plain_english: 'Monitor external service providers and systems with access to your environment for cybersecurity events and anomalous behavior.',
    example: 'Third-party API integrations are monitored for unusual call patterns and access anomalies. Service provider access sessions are logged and reviewed monthly.',
    evidence_tips: [
      'Third-party access monitoring records',
      'API gateway logs showing external service provider activity',
      'Vendor access review records',
    ],
    common_mistakes: 'Monitoring your own systems thoroughly while giving service providers unmonitored access — breaches frequently originate from compromised service provider accounts.',
    nist_reference: 'DE.CM-06',
  },
  'DE.CM-07': {
    plain_english: 'Monitor for unauthorized access attempts, privilege escalation, and anomalous authentication events.',
    example: 'SIEM rules detect more than 5 failed logins in 60 seconds, successful login after multiple failures, and logins from blacklisted IP ranges. Alerts go to the SOC in real time.',
    evidence_tips: [
      'Authentication monitoring rules in SIEM or security tools',
      'Brute force and credential stuffing detection alert records',
      'Privileged access monitoring evidence',
    ],
    common_mistakes: 'Logging authentication events without defining detection rules — the logs exist but nobody is alerted when attackers are actively attempting brute-force attacks.',
    nist_reference: 'DE.CM-07',
  },
  'DE.CM-08': {
    plain_english: 'Continuously scan systems, networks, and applications for vulnerabilities in configurations, software, and firmware.',
    example: 'Authenticated vulnerability scans run weekly across all production systems. Results feed into the vulnerability management program with automatic ticket creation for critical findings.',
    evidence_tips: [
      'Vulnerability scan schedules and recent scan reports',
      'Scan coverage documentation showing all systems included',
      'Remediation tracking records for scan findings',
    ],
    common_mistakes: 'Running unauthenticated scans that miss a majority of vulnerabilities because they cannot see configurations visible only to authenticated users.',
    nist_reference: 'DE.CM-08',
  },
  'DE.CM-09': {
    plain_english: 'Monitor computing hardware and firmware for indications of unauthorized modification, tampering, or hardware-based attacks.',
    example: 'Servers use TPM-based attestation. Boot integrity measurements are compared against known-good baselines. Physical inspection of hardware is conducted during scheduled maintenance.',
    evidence_tips: [
      'TPM or hardware attestation records',
      'Physical hardware inspection procedures and records',
      'Hardware integrity monitoring tool configuration',
    ],
    common_mistakes: 'Treating hardware as inherently trusted and only monitoring at the software layer — hardware implants and firmware compromises are real threats in sensitive environments.',
    nist_reference: 'DE.CM-09',
  },

  // ── DETECT — Adverse Event Analysis ──────────────────────────────────────────
  'DE.AE-02': {
    plain_english: 'Analyze detected events and anomalies to understand attack methods, targets, and potential impact.',
    example: 'SOC analysts investigate each high-severity alert to determine attack vector, impacted systems, and potential lateral movement. Analysis is documented in the ticketing system.',
    evidence_tips: [
      'Incident investigation records showing analysis methodology',
      'Alert triage procedures documentation',
      'Attack analysis reports from SOC investigations',
    ],
    common_mistakes: 'Treating every alert as an isolated event rather than investigating whether it is part of a larger attack campaign.',
    nist_reference: 'DE.AE-02',
  },
  'DE.AE-03': {
    plain_english: 'Aggregate and correlate event data from multiple log sources to identify patterns that indicate potential incidents.',
    example: 'The SIEM correlates authentication failures, endpoint alerts, and network anomalies to detect coordinated attacks that no single source would reveal alone.',
    evidence_tips: [
      'SIEM correlation rules documentation',
      'Multi-source alert correlation examples',
      'Log source inventory showing all feeds going into correlation',
    ],
    common_mistakes: 'Collecting logs from many sources but not building correlation rules — the SIEM becomes an expensive log archive rather than a detection platform.',
    nist_reference: 'DE.AE-03',
  },
  'DE.AE-04': {
    plain_english: 'Assess the estimated impact, scope, and urgency of detected events to inform appropriate response prioritization.',
    example: 'The SOC uses a severity matrix (P1-P4) based on data sensitivity, system criticality, and lateral movement potential. P1 events trigger 24/7 on-call response within 15 minutes.',
    evidence_tips: [
      'Incident severity classification matrix',
      'Alert triage records showing severity assessments',
      'Response time records by severity level',
    ],
    common_mistakes: 'Treating all security alerts with equal urgency — SOC teams burn out chasing low-severity alerts while missing real incidents buried in the noise.',
    nist_reference: 'DE.AE-04',
  },
  'DE.AE-05': {
    plain_english: 'Define alert thresholds and escalation criteria so significant events are reported and acted upon promptly.',
    example: 'Alert thresholds are documented for each detection rule: low severity goes to a daily digest, high severity pages on-call within 15 minutes, critical severity calls the CISO.',
    evidence_tips: [
      'Alert escalation procedures documentation',
      'Defined thresholds for different alert categories',
      'Escalation records showing appropriate response to high-severity alerts',
    ],
    common_mistakes: 'Having escalation procedures that exist on paper but are never followed in practice — alerts queue up unaddressed because everyone assumes someone else is handling it.',
    nist_reference: 'DE.AE-05',
  },
  'DE.AE-06': {
    plain_english: 'Share information about detected adverse events with relevant internal stakeholders and appropriate external partners.',
    example: 'A data breach triggers simultaneous notification to legal, privacy, communications, and the CISO. External sharing with sector ISAC is done after internal coordination.',
    evidence_tips: [
      'Incident communication procedures',
      'Evidence of timely internal stakeholder notification during incidents',
      'External sharing records with ISACs or government partners',
    ],
    common_mistakes: 'The SOC contains incidents internally without notifying legal or compliance until it is too late to meet regulatory notification deadlines.',
    nist_reference: 'DE.AE-06',
  },
  'DE.AE-07': {
    plain_english: 'Use cyber threat intelligence to enrich event analysis and improve detection of sophisticated attacks.',
    example: 'Threat intelligence IOCs from the ISAC are automatically imported into the SIEM and correlated against current event data. New threat actor TTPs are used to develop new detection rules.',
    evidence_tips: [
      'Threat intelligence integration with SIEM or detection tools',
      'IOC ingestion records showing active use of threat intel',
      'Detection rules created based on threat actor TTPs',
    ],
    common_mistakes: 'Subscribing to threat intelligence feeds but not integrating them into detection tools — intelligence sits in a portal that nobody references during investigations.',
    nist_reference: 'DE.AE-07',
  },
  'DE.AE-08': {
    plain_english: 'Determine whether detected events meet the criteria to be declared cybersecurity incidents requiring activation of the incident response plan.',
    example: 'Incident declaration criteria define thresholds for: confirmed unauthorized access, active exfiltration, ransomware indicators, and regulatory notification triggers. Criteria are reviewed annually.',
    evidence_tips: [
      'Incident declaration criteria documentation',
      'Records of incident declaration decisions with rationale',
      'Escalation process showing path from event to incident declaration',
    ],
    common_mistakes: 'Using subjective judgment to declare incidents rather than defined criteria — similar events get treated inconsistently depending on who is on duty.',
    nist_reference: 'DE.AE-08',
  },

  // ── RESPOND — Incident Management ─────────────────────────────────────────────
  'RS.MA-01': {
    plain_english: 'Activate and execute your incident response plan when an incident is detected, assigning roles and coordinating activities.',
    example: 'When a confirmed incident is declared, the Incident Commander activates the IRP, assigns roles from the response team roster, opens a war room channel, and initiates the appropriate playbook.',
    evidence_tips: [
      'Incident response plan activation records',
      'Incident tracking records showing role assignments',
      'War room or communication records for active incidents',
    ],
    common_mistakes: 'Having a written incident response plan that nobody knows how to activate — when an incident occurs, the team improvises rather than following the established procedure.',
    nist_reference: 'RS.MA-01',
  },
  'RS.MA-02': {
    plain_english: 'Triage detected incidents to determine their scope, severity, and priority for response.',
    example: 'Initial triage determines: what systems are affected, what data may be at risk, whether the attacker is still active, and whether the incident triggers regulatory notification requirements.',
    evidence_tips: [
      'Incident triage checklist or procedure',
      'Triage records from actual incidents showing scope assessment',
      'Severity classification criteria for incident types',
    ],
    common_mistakes: 'Jumping to containment without first establishing the scope — containment actions alert the attacker and cause them to accelerate exfiltration before the team understands what they are dealing with.',
    nist_reference: 'RS.MA-02',
  },
  'RS.MA-03': {
    plain_english: 'Apply appropriate containment strategies to limit the spread and impact of cybersecurity incidents.',
    example: 'The ransomware playbook defines containment actions: isolate affected endpoints from the network, revoke compromised credentials, block attacker IPs, and snapshot affected systems for forensics.',
    evidence_tips: [
      'Containment strategies in incident response playbooks',
      'Evidence of containment actions taken during actual incidents',
      'Network isolation and credential revocation procedure records',
    ],
    common_mistakes: 'Immediately wiping compromised systems as the first response — this destroys forensic evidence needed to understand the attack and assess the full scope of compromise.',
    nist_reference: 'RS.MA-03',
  },
  'RS.MA-04': {
    plain_english: 'Coordinate incident response across internal teams and engage external resources when appropriate.',
    example: 'The IR plan defines when to engage: legal counsel (data breach), law enforcement (criminal activity), incident response retainer (complex attacks), and public relations (customer-impacting events).',
    evidence_tips: [
      'Incident response escalation procedures with external contact list',
      'Retainer agreements with IR firms, legal, or forensics providers',
      'Records of external resource engagement during past incidents',
    ],
    common_mistakes: 'Trying to handle all incidents internally without pre-established external resources — when a serious incident occurs, the organization scrambles to find a forensics firm with no prior relationship.',
    nist_reference: 'RS.MA-04',
  },
  'RS.MA-05': {
    plain_english: 'Formally close incidents after verifying that eradication, recovery, and all required follow-up activities are complete.',
    example: 'Incidents are closed only after: the threat is confirmed eradicated, affected systems are restored and verified, notifications are sent, a post-incident review is scheduled, and the CISO approves closure.',
    evidence_tips: [
      'Incident closure criteria documentation',
      'Incident closure records with required sign-offs',
      'Post-incident review scheduling evidence before closure',
    ],
    common_mistakes: 'Closing incidents informally once the immediate problem seems resolved without verifying eradication or ensuring all follow-up actions are tracked.',
    nist_reference: 'RS.MA-05',
  },

  // ── RESPOND — Incident Analysis ────────────────────────────────────────────────
  'RS.AN-03': {
    plain_english: 'Perform root cause analysis to understand how the attack occurred and what weaknesses were exploited.',
    example: 'Post-incident analysis traces the attack back to an unpatched VPN vulnerability, determines the attacker\'s TTPs, and identifies three other systems that were accessed during lateral movement.',
    evidence_tips: [
      'Root cause analysis reports from past incidents',
      'Attack timeline reconstruction documentation',
      'MITRE ATT&CK mapping of attacker TTPs',
    ],
    common_mistakes: 'Treating root cause as "the attacker exploited a vulnerability" rather than investigating why the vulnerability existed, why it was not patched, and why detection was delayed.',
    nist_reference: 'RS.AN-03',
  },
  'RS.AN-04': {
    plain_english: 'Estimate the scope and magnitude of incidents to determine response actions and notification requirements.',
    example: 'The incident analysis determines that 47,000 customer records were potentially accessed, triggering GDPR notification requirements. The scope assessment drives the notification timeline.',
    evidence_tips: [
      'Incident scope assessment methodology',
      'Data exposure analysis records from past incidents',
      'Regulatory notification threshold analysis documentation',
    ],
    common_mistakes: 'Underestimating incident scope to avoid regulatory notification — organizations later face larger penalties for delayed notification than they would have for timely disclosure.',
    nist_reference: 'RS.AN-04',
  },
  'RS.AN-05': {
    plain_english: 'Conduct forensic analysis on compromised systems to understand what happened, preserve evidence, and support potential legal proceedings.',
    example: 'Forensic disk images are taken before remediation. Memory captures are collected from active systems. Chain of custody is maintained for all forensic artifacts.',
    evidence_tips: [
      'Forensic analysis procedures documentation',
      'Forensic image and evidence collection records',
      'Chain of custody documentation for forensic evidence',
    ],
    common_mistakes: 'Remediating systems before forensic analysis is complete — valuable evidence is destroyed, making it impossible to determine the full scope of the breach.',
    nist_reference: 'RS.AN-05',
  },
  'RS.AN-06': {
    plain_english: 'Identify and document all systems, data, and operational capabilities affected by an incident.',
    example: 'The incident analysis produces a complete list of affected systems, data repositories accessed, accounts compromised, and services disrupted, with supporting evidence for each.',
    evidence_tips: [
      'Affected asset inventory from incident investigation',
      'Data access analysis showing what records were potentially accessed',
      'Compromise assessment documentation',
    ],
    common_mistakes: 'Declaring a narrow scope of impact to minimize notification obligations without thoroughly investigating whether other systems were accessed during lateral movement.',
    nist_reference: 'RS.AN-06',
  },
  'RS.AN-07': {
    plain_english: 'Determine whether an incident triggers legal, regulatory, or contractual notification obligations and initiate those notifications in a timely manner.',
    example: 'The IR plan includes a notification decision tree: if PII is exposed, GDPR/CCPA timelines start immediately. The legal team is engaged within 2 hours of any confirmed breach.',
    evidence_tips: [
      'Regulatory notification decision tree or checklist',
      'Notification timeline records from past incidents',
      'Legal counsel engagement records during incidents',
    ],
    common_mistakes: 'Waiting to notify regulators until the full scope is understood — most regulations require notification within 72 hours of becoming aware, not after completing the investigation.',
    nist_reference: 'RS.AN-07',
  },
  'RS.AN-08': {
    plain_english: 'Compare incident characteristics against threat intelligence to determine if the incident matches a known threat actor or campaign.',
    example: 'IOCs from the incident are submitted to threat intelligence platforms and compared against known threat actor profiles. The match to a known ransomware group informs containment and recovery strategy.',
    evidence_tips: [
      'Threat intelligence matching records from past incidents',
      'IOC sharing with threat intelligence communities',
      'Threat actor attribution analysis documentation',
    ],
    common_mistakes: 'Treating every incident as novel rather than checking whether it matches known threat actor patterns that would inform the response approach.',
    nist_reference: 'RS.AN-08',
  },

  // ── RESPOND — Incident Response Reporting and Communication ───────────────────
  'RS.CO-02': {
    plain_english: 'Report incidents to appropriate internal stakeholders — leadership, legal, HR, operations — in a timely manner through established channels.',
    example: 'The IR plan defines who must be notified for each incident type: the CISO for all confirmed incidents, legal for any potential data breach, HR for insider threat incidents.',
    evidence_tips: [
      'Internal notification procedures in the IR plan',
      'Notification records from past incidents',
      'Communication channel documentation (war room, email lists, etc.)',
    ],
    common_mistakes: 'The SOC notifies IT leadership but not legal or compliance — by the time those teams are looped in, regulatory deadlines have passed.',
    nist_reference: 'RS.CO-02',
  },
  'RS.CO-03': {
    plain_english: 'Share cybersecurity incident information with external parties — regulators, law enforcement, ISACs, affected third parties — as required or appropriate.',
    example: 'The organization files a GDPR supervisory authority notification within 72 hours, notifies affected customers within 30 days, and shares anonymized IOCs with the sector ISAC.',
    evidence_tips: [
      'External notification records with timestamps',
      'Regulatory notification submissions',
      'ISAC or information sharing community notification records',
    ],
    common_mistakes: 'Keeping incident details internal to avoid reputational damage, missing regulatory notification requirements and potentially violating contractual obligations.',
    nist_reference: 'RS.CO-03',
  },

  // ── RESPOND — Incident Mitigation ─────────────────────────────────────────────
  'RS.MI-01': {
    plain_english: 'Contain detected incidents using isolation, blocking, or other measures to prevent their spread.',
    example: 'Compromised endpoints are automatically quarantined by EDR. Suspicious accounts are immediately disabled. Attacker C2 IPs are blocked at the firewall within minutes of identification.',
    evidence_tips: [
      'EDR quarantine records from incidents',
      'Firewall block rule changes tied to incident response',
      'Account disable records with incident correlation',
    ],
    common_mistakes: 'Delayed containment while the incident is still being analyzed — every minute of delay allows the attacker to deepen the compromise and exfiltrate more data.',
    nist_reference: 'RS.MI-01',
  },
  'RS.MI-02': {
    plain_english: 'Eradicate the root causes of incidents from affected systems and verify through testing that the threat has been fully removed.',
    example: 'After containing ransomware, affected systems are reimaged from clean backups. All credentials in scope are rotated. A thorough sweep confirms no persistence mechanisms remain.',
    evidence_tips: [
      'Eradication procedures and evidence of completion',
      'System rebuild or reimaging records',
      'Post-eradication verification scan results',
    ],
    common_mistakes: 'Restoring systems from backup without first understanding how the attacker gained access — the same attack will succeed again within hours.',
    nist_reference: 'RS.MI-02',
  },

  // ── RECOVER — Incident Recovery Plan Execution ────────────────────────────────
  'RC.RP-01': {
    plain_english: 'Execute your documented recovery plan to restore systems and capabilities affected by an incident.',
    example: 'After eradication, the recovery plan is activated: systems are restored from clean backups, configurations are rebuilt from documented baselines, and services are brought back online in priority order.',
    evidence_tips: [
      'Recovery plan documentation with step-by-step procedures',
      'Recovery execution records from actual incidents',
      'System restoration records with timestamps',
    ],
    common_mistakes: 'Recovering ad hoc without a documented plan — each recovery takes longer than necessary and important steps get missed under pressure.',
    nist_reference: 'RC.RP-01',
  },
  'RC.RP-02': {
    plain_english: 'Select and implement recovery actions based on defined recovery time and recovery point objectives.',
    example: 'The BCP defines RTO=4 hours and RPO=1 hour for customer-facing systems. Recovery actions (restore from backup, activate standby, failover) are chosen based on which objective is at risk.',
    evidence_tips: [
      'RTO and RPO documentation per system or service',
      'Recovery action decision tree based on RTO/RPO',
      'Recovery exercise records validating RTO/RPO can be met',
    ],
    common_mistakes: 'Setting RTO and RPO targets without testing whether backup and recovery procedures can actually meet them.',
    nist_reference: 'RC.RP-02',
  },
  'RC.RP-03': {
    plain_english: 'Verify that recovered systems are functioning correctly, have been re-hardened, and show no signs of re-compromise before returning them to operation.',
    example: 'Before returning a system to production, security runs a full vulnerability scan, verifies hardening baselines, confirms all credentials have been rotated, and monitors for 24 hours.',
    evidence_tips: [
      'Post-recovery verification checklist',
      'Vulnerability scan results from recovered systems',
      'Configuration compliance verification records',
    ],
    common_mistakes: 'Rushing recovered systems back online under business pressure before completing verification — a compromised system returned to production can re-infect the environment.',
    nist_reference: 'RC.RP-03',
  },
  'RC.RP-04': {
    plain_english: 'Communicate recovery status, timelines, and residual risks to relevant internal and external stakeholders during recovery.',
    example: 'During recovery, hourly status updates are sent to the executive team. Customer-facing outage communications are published every 30 minutes. SLA breach notifications go to affected customers.',
    evidence_tips: [
      'Recovery communication templates and procedures',
      'Status update records during past incidents',
      'External customer notification records',
    ],
    common_mistakes: 'Communicating only when recovery is complete — stakeholders assume the worst during silence and take actions that complicate the recovery.',
    nist_reference: 'RC.RP-04',
  },
  'RC.RP-05': {
    plain_english: 'Conduct post-incident reviews after recovery, document lessons learned, and use them to improve recovery plans.',
    example: 'Within 2 weeks of each major incident, a post-incident review is held with all response teams. Lessons are documented, action items assigned, and the recovery plan is updated before the next review cycle.',
    evidence_tips: [
      'Post-incident review report templates and completed reports',
      'Action item tracking from post-incident reviews',
      'Evidence of recovery plan updates based on lessons learned',
    ],
    common_mistakes: 'Conducting post-incident reviews as a blame exercise rather than a genuine process improvement activity — participants become defensive and lessons are missed.',
    nist_reference: 'RC.RP-05',
  },

  // ── RECOVER — Incident Recovery Communication ──────────────────────────────────
  'RC.CO-03': {
    plain_english: 'Communicate with affected parties about recovery progress, expected timelines, and ongoing risks following an incident.',
    example: 'After a service outage caused by a security incident, the organization publishes a detailed post-mortem on its status page and emails affected customers with the incident timeline and remediation steps.',
    evidence_tips: [
      'External communication records from past incidents',
      'Status page or incident communication records',
      'Post-mortem reports shared with affected parties',
    ],
    common_mistakes: 'Going silent after an incident resolves without explaining what happened, what was done, and what will be done differently — eroding customer trust.',
    nist_reference: 'RC.CO-03',
  },
  'RC.CO-04': {
    plain_english: 'Apply lessons learned from incident recovery communications to improve future response and communications plans.',
    example: 'After feedback that customers found incident updates too technical and infrequent, the communications plan is updated with clearer language templates and a 30-minute update schedule.',
    evidence_tips: [
      'Post-incident feedback records',
      'Communication plan update records based on lessons learned',
      'Customer or stakeholder feedback integration documentation',
    ],
    common_mistakes: 'Making the same communications mistakes in each incident because post-incident reviews focus on technical lessons but not on how well communications were handled.',
    nist_reference: 'RC.CO-04',
  },
};

export default NIST_CSF_EXPLANATIONS;
